import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DJAY_TIMINGS } from './config';
export type BubbleColorScheme = 'default' | 'indigo' | 'amber' | 'emerald' | 'violet';
const COLOR_SCHEMES: Record<BubbleColorScheme, {
  bg: string;
  text: string;
  border: string;
  pointer: string;
}> = {
  default: {
    bg: 'bg-white',
    text: 'text-slate-700',
    border: 'border-slate-200',
    pointer: 'bg-white border-slate-200'
  },
  indigo: {
    bg: 'bg-indigo-50',
    text: 'text-indigo-900',
    border: 'border-indigo-200',
    pointer: 'bg-indigo-50 border-indigo-200'
  },
  amber: {
    bg: 'bg-amber-50',
    text: 'text-amber-900',
    border: 'border-amber-200',
    pointer: 'bg-amber-50 border-amber-200'
  },
  emerald: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-900',
    border: 'border-emerald-200',
    pointer: 'bg-emerald-50 border-emerald-200'
  },
  violet: {
    bg: 'bg-violet-50',
    text: 'text-violet-900',
    border: 'border-violet-200',
    pointer: 'bg-violet-50 border-violet-200'
  }
};
export interface DjayBubbleProps {
  message: string;
  autoDismissMs?: number;
  position?: 'top' | 'bottom' | 'left' | 'right';
  colorScheme?: BubbleColorScheme;
  className?: string;
  onDismiss?: () => void;
  isVisible?: boolean;
}
/**
 * DjayBubble — Chat-style speech bubble that pairs with DjayAvatar.
 * DDD co-locatie: moved from components/mascot/ in s60.
 */
export function DjayBubble({
  message,
  autoDismissMs = DJAY_TIMINGS.BUBBLE_DEFAULT_DISMISS_MS,
  position = 'right',
  colorScheme = 'default',
  className = '',
  onDismiss,
  isVisible = true
}: DjayBubbleProps) {
  // Defensive: if a caller passes an unknown colorScheme (e.g. via a wrong
  // API call site), fall back to 'default' instead of crashing on
  // `colors.text` / `colors.bg` further down. This guards the practice
  // screen against regressions like the §16.H idle-bubble miswire.
  const colors = COLOR_SCHEMES[colorScheme] ?? COLOR_SCHEMES.default;
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  // Auto-dismiss timer — resets when message or visibility changes
  useEffect(() => {
    if (!isVisible || autoDismissMs <= 0) return;
    const timer = setTimeout(() => {
      onDismissRef.current?.();
    }, autoDismissMs);
    return () => clearTimeout(timer);
  }, [isVisible, message, autoDismissMs]);
  const pointerStyles: Record<string, string> = {
    top: 'bottom-full right-6 border-l-transparent border-r-transparent border-t-transparent border-b-white mb-[-1px]',
    bottom: 'top-full right-6 border-l-transparent border-r-transparent border-b-transparent border-t-white mt-[-1px]',
    left: 'right-full top-3 border-t-transparent border-b-transparent border-l-transparent border-r-white mr-[-1px]',
    right: 'left-full top-3 border-t-transparent border-b-transparent border-r-transparent border-l-white ml-[-1px]'
  };
  const pointerBorderStyles: Record<string, string> = {
    top: 'border-b-[10px] border-l-[8px] border-r-[8px]',
    bottom: 'border-t-[10px] border-l-[8px] border-r-[8px]',
    left: 'border-r-[10px] border-t-[8px] border-b-[8px]',
    right: 'border-l-[10px] border-t-[8px] border-b-[8px]'
  };
  const bubbleVariants = {
    hidden: {
      opacity: 0,
      scale: 0.85,
      y: position === 'top' ? 10 : position === 'bottom' ? -10 : 0,
      x: position === 'left' ? 10 : position === 'right' ? -10 : 0
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      x: 0,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 25
      }
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      transition: {
        duration: 0.15,
        ease: 'easeOut'
      }
    }
  };
  return <AnimatePresence data-id="element-3584" data-mp="33qlw9myhoj0w">
      {isVisible && <motion.div variants={bubbleVariants} initial="hidden" animate="visible" exit="exit" role="status" aria-live="polite" aria-atomic="true" className={`relative inline-block ${className}`} data-id="element-3585" data-mp="2psxewp3mru0l">
        
          <div className={`${colors.bg} rounded-2xl shadow-lg border ${colors.border} px-4 py-3 min-w-[140px] max-w-[320px]`} data-id="element-3586" data-mp="2mqj4kq9lxcmx">
          
            <p className={`${colors.text} font-medium text-sm leading-relaxed`} data-id="element-3587" data-mp="2hukmfkevgquy">
              {message}
            </p>
          </div>
          <div className={`absolute w-0 h-0 ${pointerStyles[position]} ${pointerBorderStyles[position]}`} aria-hidden="true" data-id="element-3588" data-mp="282xzwrx335cw" />
        
        </motion.div>}
    </AnimatePresence>;
}